import { createContext, useContext, useReducer, type ReactNode } from 'react';
import type { ErrorAction, ErrorState } from '@/types/types';

const INITIAL_STATE: ErrorState = {
    showSnack: false,
    hasError: false,
    severity: '',
    status: '',
    alert: '',
    error: '',
    info: '',
    stack: '',
    type: '',
    message: '',
};

const errorReducer = (state: ErrorState, action: ErrorAction): ErrorState => {
    switch (action.type) {
        case 'NO_ERROR':
            return {
                ...state,
                hasError: false,
                alert: action.payload?.alert ?? '',
                severity: 'success',
                status: action.payload?.status ?? 500,
                showSnack: true,
            };
        case 'API_ERROR':
            return {
                ...state,
                message: action.payload?.message ?? '',
                alert: 'An error has occurred with the server.',
                stack: action.payload?.stack ?? '',
                name: action.payload?.name,
                cause: action.payload?.cause,
                hasError: true,
                type: 'api_error',
                severity: 'error',
                status: action.payload?.status ?? 500,
                showSnack: true,
            };
        case 'INVALID_INPUT':
            return {
                ...state,
                hasError: true,
                alert: action.payload?.alert ?? '',
                severity: 'warning',
                showSnack: true,
            };
        case 'RESET_ERROR':
            return INITIAL_STATE;
        default:
            return state;
    }
};

export interface ErrorContextValue {
    state: ErrorState;
    DISPATCH: (action: ErrorAction) => void;
}

const ErrorContext = createContext<ErrorContextValue>({
    state: INITIAL_STATE,
    DISPATCH: () => {},
});

export const ErrorProvider = ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(errorReducer, INITIAL_STATE);

    return (
        <ErrorContext.Provider value={{ state, DISPATCH: dispatch }}>
            {children}
        </ErrorContext.Provider>
    );
};

export const useError = () => useContext(ErrorContext);
